import styled from 'styled-components'
import moment from 'moment-timezone'

import { parseServerWallClock } from './date.utils'
import { Flex } from '../styles/global'
import { UnitType } from '../models'


// any ui helper methods
export const renderTables = () => { }

export const removeQuestion = (arr: any, question: any) => {
    const result = arr.filter((item: any) => item.id !== question.id)
    console.log("Result: ", result, "Question: ", question)
    return result
}

// returns a new string with the query params removed
export const removeQueryParams = (str: string) => {
    let parsed = "";
    for (let i = 0; i < str.length; i++) {
        if (str[i] === "?") {
            break
        }
        parsed += str[i]
    }
    return parsed
}

export const ViewContainer = styled.div`
    position: absolute;
    width: calc(100% - 245px);
    min-height: 100%;
    z-index: 2;
    top: 0;
    background: #333333;
    /* padding-left: 28px; */
`

export const Container = styled.div`
    /* background: #FF0; */
`

export const Wrapper = styled(Flex)`
    background: #FAFAFA;
    padding: 28px 0;
    margin-bottom: 16px;

    padding-left: 16px;

    max-width: 500px;

    > div > p {
        padding: 8px 0;
        font-weight: 600;
    }

    &:first-child {
        margin-top: 40px;
    }
`

export const makeId = (name: string): string => name.replace(/\s+/g, '-').toLowerCase();


export const pickTimeUnit = (unitType: UnitType) => {
    switch (unitType) {
        case UnitType.SECOND:
            return 'second'
        case UnitType.MINUTE:
            return 'minute'
        case UnitType.HOUR:
            return 'hour'
        case UnitType.DAY:
            return 'day'
        default:
            return ''
    }
}

export const isPlural = (number: number, singular: string, plural?: string): string => number !== 1 ? plural ? plural : `${singular}s` : singular

export const normalizeSimulation = (sim: any): any => {
    if (!sim) return sim;

    const copy = { ...sim };

    // Remove read-only or calculated fields that might cause 400 Bad Request
    const readOnlyFields = [
        'active_participants',
        'infected_participants',
        'active_simulation_id',
        'created_at',
        'updated_at',
        'active_participants_count',
        'infected_participants_count'
    ];
    readOnlyFields.forEach(field => delete copy[field]);

    // Default values and ensured numeric types
    const numericFields: { [key: string]: number } = {
        // id should NOT be set when creating a new simulation because it makes the backend think it's an update
        // 'id': 0, 
        'size': 10,
        'time_step': 2,
        'epi_generations': 1,
        'number_of_contacts': 1,
        'number_of_contacts_in': 10,
        'join_window': 100,
        'food_interval': 0.5,
        'genome_coverage': 100,
        'number_of_strains': 1,
        'case_zero_count': 1,
        'case_zero_population_size': 10,
        'case_zero_adapt_time': 1,
        'case_zero_infect_time': 2,
        'init_score_min': 0,
        'init_score_max': 10,
        'pathogen_id': 0,
        'points_answer': 1,
        'points_symptomatic': 0,
        'ui_style': 0
    };

    Object.keys(numericFields).forEach(field => {
        const defaultValue = numericFields[field];
        if (copy[field] === undefined || copy[field] === null || copy[field] === '') {
            copy[field] = defaultValue;
        } else {
            copy[field] = Number(copy[field]);
        }
    });

    if (!copy.rewards) copy.rewards = {};
    const rewardFields: { [key: string]: number } = {
        'ppe_period': 0.5,
        'mask_period': 1,
        'min_questions': 3,
        'min_score_mask': 60,
        'min_score_ppe': 100
    };
    Object.keys(rewardFields).forEach(field => {
        const defaultValue = rewardFields[field];
        if (copy.rewards[field] === undefined || copy.rewards[field] === null || copy.rewards[field] === '') {
            copy.rewards[field] = defaultValue;
        } else {
            copy.rewards[field] = Number(copy.rewards[field]);
        }
    });

    // Ensure Booleans with defaults
    const booleanFields: { [key: string]: boolean } = {
        'archived': false,
        'global_sim': false,
        'qr_support': false,
        'food_support': false,
        'sequence_support': false,
        'score_support': false,
        'debug_support': false,
        'leaderboard_support': false,
        'raffle_support': false,
        'use_location': true,
        'use_beacons': true,
        'is_modified': false,
        'group_support': false,
        'random_group': false,
        'shop_support': false,
        'intervention_support': false
    };

    Object.keys(booleanFields).forEach(field => {
        const defaultValue = booleanFields[field];
        if (copy[field] === undefined || copy[field] === null) {
            copy[field] = defaultValue;
        } else {
            copy[field] = !!copy[field];
        }
    });

    // Ensure Arrays
    copy.interventions = copy.interventions || [];
    copy.group_selection = copy.group_selection || [];
    copy.pauseDates = copy.pauseDates || [];
    copy.shop_items = copy.shop_items || [];

    return copy;
};

export const normalizePathogen = (pathogen: any): any => {
    if (!pathogen) return pathogen;
    const copy = { ...pathogen };

    if (!copy.seed_parameters) copy.seed_parameters = {};
    const seedFields = [
        'basic_reproduction_number', 'exposed_to_inf_mild_ratio',
        'mean_symp_onset_to_inf_mild_ratio', 'sdev_symp_onset_to_inf_mild_ratio',
        'inf_severe_to_inf_mild_ratio', 'asym_to_inf_mild_ratio',
        'asym_pinf_factor', 'mild_pinf_factor', 'severe_pinf_factor',
        'prevalance_similar_disease'
    ];
    seedFields.forEach(field => {
        copy.seed_parameters[field] = Number(copy.seed_parameters[field] || 0);
    });

    if (!copy.infection_prob) copy.infection_prob = {};
    const probFields = ['from_source', 'from_reinfection', 'from_asym_peer', 'from_mild_peer', 'from_severe_peer'];
    probFields.forEach(field => {
        copy.infection_prob[field] = Number(copy.infection_prob[field] || 0);
    });

    if (!copy.infection_info) copy.infection_info = {};
    const infoFields = [
        'asym_prevalance', 'asym_period', 'mild_period', 'severe_period',
        'severe_from_mild', 'mild_from_asym', 'exposed_period',
        'min_mild_symptoms', 'min_severe_symptoms', 'onset_time_mean', 'onset_time_sdev'
    ];
    infoFields.forEach(field => {
        copy.infection_info[field] = Number(copy.infection_info[field] || 0);
    });

    if (!copy.fpos_info) copy.fpos_info = {};
    const fposFields = ['prob', 'mean', 'sdev', 'min_symptoms'];
    fposFields.forEach(field => {
        copy.fpos_info[field] = Number(copy.fpos_info[field] || 0);
    });

    copy.id = Number(copy.id);
    copy.source_range = Number(copy.source_range || 0);
    copy.mutation_rate = Number(copy.mutation_rate || 0);

    return copy;
};

export const parseSimWallClock = (sim: any, dateValue: any) =>
    parseServerWallClock(dateValue, sim?.time_zone_IANA);

export const bucketSimulation = (sim: any): 'active' | 'future' | 'past' | 'archived' => {
    if (sim?.archived) return 'archived';

    const start = parseSimWallClock(sim, sim?.start_date);
    const end = parseSimWallClock(sim, sim?.end_date);
    const tzIana = sim?.time_zone_IANA;

    const now = tzIana && moment.tz.zone(tzIana) ? moment().tz(tzIana) : moment();
    if (!start || !end) return 'past';

    if (now.isBefore(start)) return 'future';
    if (now.isAfter(end)) return 'past';
    return 'active';
};